/**
 * Utility functions for shuffle functionality in music player
 */

import { Track } from '../types';

/**
 * Generate a shuffled order for all tracks (Shuffle All)
 * The current track stays in its position, all other tracks are shuffled
 * This matches Spotify's shuffle behavior
 * @param length - Number of tracks
 * @param currentIndex - Current track index
 * @param tracks - Optional array of tracks to filter out unavailable ones
 * @returns Array of shuffled indices with current track at position 0
 */
export const generateShuffleOrder = (
  length: number,
  currentIndex: number = 0,
  tracks?: Track[],
): number[] => {
  // Create array of all track indices except current
  let otherTracks = Array.from({ length }, (_, i) => i).filter(
    (i) => i !== currentIndex,
  );

  // Filter out unavailable tracks if tracks array is provided
  if (tracks) {
    otherTracks = otherTracks.filter((i) => tracks[i]?.isAvailable !== false);
  }

  // Fisher-Yates shuffle algorithm for all other tracks
  for (let i = otherTracks.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [otherTracks[i], otherTracks[j]] = [otherTracks[j], otherTracks[i]];
  }

  // Return complete order: [currentIndex, ...shuffled other tracks]
  return [currentIndex, ...otherTracks];
};

/**
 * Get the next track index based on shuffle and repeat state
 * Skips unavailable tracks (isAvailable: false)
 * @param currentIndex - Current track index
 * @param shuffleEnabled - Whether shuffle is enabled
 * @param shuffleOrder - Array of shuffled indices
 * @param totalTracks - Total number of tracks
 * @param repeatMode - Repeat mode ('off', 'all', 'one')
 * @param tracks - Optional array of tracks to check availability
 * @returns Next track index or null if at end
 */
export const getNextTrackIndex = (
  currentIndex: number,
  shuffleEnabled: boolean,
  shuffleOrder: number[],
  totalTracks: number,
  repeatMode: 'off' | 'all' | 'one' = 'off',
  tracks?: Track[],
): number | null => {
  // Repeat one - stay on current track
  if (repeatMode === 'one') {
    return currentIndex;
  }

  if (shuffleEnabled && shuffleOrder.length > 0) {
    const currentShufflePos = shuffleOrder.indexOf(currentIndex);

    // If current track is found in shuffle order, get next available track
    if (currentShufflePos >= 0 && currentShufflePos < shuffleOrder.length - 1) {
      // Find next available track in shuffle order
      for (let i = currentShufflePos + 1; i < shuffleOrder.length; i++) {
        const nextIndex = shuffleOrder[i];
        if (!tracks || tracks[nextIndex]?.isAvailable !== false) {
          return nextIndex;
        }
      }
      // If no available track found and repeat all, try from beginning
      if (repeatMode === 'all') {
        for (let i = 0; i <= currentShufflePos; i++) {
          const nextIndex = shuffleOrder[i];
          if (!tracks || tracks[nextIndex]?.isAvailable !== false) {
            return nextIndex;
          }
        }
      }
      return null;
    }

    // If current track is found and we're at the end of shuffle order
    if (
      currentShufflePos >= 0 &&
      currentShufflePos === shuffleOrder.length - 1
    ) {
      if (repeatMode === 'all') {
        // Restart from beginning of shuffle order, find first available
        for (let i = 0; i < shuffleOrder.length; i++) {
          const nextIndex = shuffleOrder[i];
          if (!tracks || tracks[nextIndex]?.isAvailable !== false) {
            return nextIndex;
          }
        }
      }
      return null;
    }

    // If current track is NOT in shuffle order (manual navigation or other issue)
    // Find a valid next available track from shuffle order that comes after current index
    const availableNext = shuffleOrder.find(
      (trackIndex) =>
        trackIndex > currentIndex &&
        (!tracks || tracks[trackIndex]?.isAvailable !== false),
    );
    if (availableNext !== undefined) {
      return availableNext;
    }

    // If no track after current index, try from beginning if repeat all
    if (repeatMode === 'all') {
      const firstAvailable = shuffleOrder.find(
        (trackIndex) => !tracks || tracks[trackIndex]?.isAvailable !== false,
      );
      return firstAvailable !== undefined ? firstAvailable : null;
    }

    // Fallback: return first available track in shuffle order
    const firstAvailable = shuffleOrder.find(
      (trackIndex) => !tracks || tracks[trackIndex]?.isAvailable !== false,
    );
    return firstAvailable !== undefined ? firstAvailable : null;
  } else {
    // Sequential playback - skip unavailable tracks
    for (
      let nextIndex = currentIndex + 1;
      nextIndex < totalTracks;
      nextIndex++
    ) {
      if (!tracks || tracks[nextIndex]?.isAvailable !== false) {
        return nextIndex;
      }
    }
    // End of playlist - check if repeat all
    if (repeatMode === 'all') {
      // Restart from beginning, find first available
      for (let nextIndex = 0; nextIndex <= currentIndex; nextIndex++) {
        if (!tracks || tracks[nextIndex]?.isAvailable !== false) {
          return nextIndex;
        }
      }
    }
    return null;
  }
};

/**
 * Get the previous track index based on shuffle and repeat state
 * Skips unavailable tracks (isAvailable: false)
 * @param currentIndex - Current track index
 * @param shuffleEnabled - Whether shuffle is enabled
 * @param shuffleOrder - Array of shuffled indices
 * @param totalTracks - Total number of tracks
 * @param repeatMode - Repeat mode ('off', 'all', 'one')
 * @param tracks - Optional array of tracks to check availability
 * @returns Previous track index or null if at beginning
 */
export const getPreviousTrackIndex = (
  currentIndex: number,
  shuffleEnabled: boolean,
  shuffleOrder: number[],
  totalTracks: number = 0,
  repeatMode: 'off' | 'all' | 'one' = 'off',
  tracks?: Track[],
): number | null => {
  // Repeat one - stay on current track
  if (repeatMode === 'one') {
    return currentIndex;
  }

  if (shuffleEnabled && shuffleOrder.length > 0) {
    const currentShufflePos = shuffleOrder.indexOf(currentIndex);

    // If current track is found in shuffle order and not at beginning
    if (currentShufflePos > 0) {
      // Find previous available track in shuffle order
      for (let i = currentShufflePos - 1; i >= 0; i--) {
        const prevIndex = shuffleOrder[i];
        if (!tracks || tracks[prevIndex]?.isAvailable !== false) {
          return prevIndex;
        }
      }
      // If no available track found and repeat all, try from end
      if (repeatMode === 'all') {
        for (let i = shuffleOrder.length - 1; i >= currentShufflePos; i--) {
          const prevIndex = shuffleOrder[i];
          if (!tracks || tracks[prevIndex]?.isAvailable !== false) {
            return prevIndex;
          }
        }
      }
      return null;
    }

    // If current track is found and we're at the beginning of shuffle order
    if (currentShufflePos === 0) {
      if (repeatMode === 'all') {
        // Go to end of shuffle order, find last available
        for (let i = shuffleOrder.length - 1; i >= 0; i--) {
          const prevIndex = shuffleOrder[i];
          if (!tracks || tracks[prevIndex]?.isAvailable !== false) {
            return prevIndex;
          }
        }
      }
      return null;
    }

    // If current track is NOT in shuffle order (manual navigation or other issue)
    // Find a valid previous available track from shuffle order that comes before current index
    const availablePrev = shuffleOrder
      .slice()
      .reverse()
      .find(
        (trackIndex) =>
          trackIndex < currentIndex &&
          (!tracks || tracks[trackIndex]?.isAvailable !== false),
      );
    if (availablePrev !== undefined) {
      return availablePrev;
    }

    // If no track before current index, try from end if repeat all
    if (repeatMode === 'all') {
      const lastAvailable = shuffleOrder
        .slice()
        .reverse()
        .find(
          (trackIndex) => !tracks || tracks[trackIndex]?.isAvailable !== false,
        );
      return lastAvailable !== undefined ? lastAvailable : null;
    }

    // Fallback: return last available track in shuffle order
    const lastAvailable = shuffleOrder
      .slice()
      .reverse()
      .find(
        (trackIndex) => !tracks || tracks[trackIndex]?.isAvailable !== false,
      );
    return lastAvailable !== undefined ? lastAvailable : null;
  } else {
    // Sequential playback - skip unavailable tracks
    for (let prevIndex = currentIndex - 1; prevIndex >= 0; prevIndex--) {
      if (!tracks || tracks[prevIndex]?.isAvailable !== false) {
        return prevIndex;
      }
    }
    // Beginning of playlist - check if repeat all
    if (repeatMode === 'all') {
      // Go to last available track
      for (
        let prevIndex = totalTracks - 1;
        prevIndex >= currentIndex;
        prevIndex--
      ) {
        if (!tracks || tracks[prevIndex]?.isAvailable !== false) {
          return prevIndex;
        }
      }
    }
    return null;
  }
};

/**
 * Check if there's a next track available
 * @param currentIndex - Current track index
 * @param shuffleEnabled - Whether shuffle is enabled
 * @param shuffleOrder - Array of shuffled indices
 * @param totalTracks - Total number of tracks
 * @param repeatMode - Repeat mode ('off', 'all', 'one')
 * @returns True if next track is available
 */
export const hasNextTrack = (
  currentIndex: number,
  shuffleEnabled: boolean,
  shuffleOrder: number[],
  totalTracks: number,
  repeatMode: 'off' | 'all' | 'one' = 'off',
): boolean => {
  // With repeat modes, there's always a next track
  if (repeatMode === 'one' || repeatMode === 'all') {
    return true;
  }
  return (
    getNextTrackIndex(
      currentIndex,
      shuffleEnabled,
      shuffleOrder,
      totalTracks,
      repeatMode,
    ) !== null
  );
};

/**
 * Check if there's a previous track available
 * @param currentIndex - Current track index
 * @param shuffleEnabled - Whether shuffle is enabled
 * @param shuffleOrder - Array of shuffled indices
 * @param totalTracks - Total number of tracks
 * @param repeatMode - Repeat mode ('off', 'all', 'one')
 * @returns True if previous track is available
 */
export const hasPreviousTrack = (
  currentIndex: number,
  shuffleEnabled: boolean,
  shuffleOrder: number[],
  totalTracks: number = 0,
  repeatMode: 'off' | 'all' | 'one' = 'off',
): boolean => {
  // With repeat modes, there's always a previous track
  if (repeatMode === 'one' || repeatMode === 'all') {
    return true;
  }
  return (
    getPreviousTrackIndex(
      currentIndex,
      shuffleEnabled,
      shuffleOrder,
      totalTracks,
      repeatMode,
    ) !== null
  );
};
